import {isPendingStatus} from '@github-ui/mergebox/page-data/payloads/status-checks'
import type {PendingWorkflowApprovalRollup} from '@github-ui/mergebox/page-data/payloads/status-checks'
import type {ValidatedChecksData} from '../use-validated-checks-data'

/**
 * The mutually-exclusive checks For You items. Each variant maps to exactly one rendered
 * component so the render conditions live in a single place and cannot drift:
 *  - `pending`: checks are still running and no workflow approval is pending
 *  - `approvable`: workflow runs are awaiting approval and the viewer can approve them
 *  - `awaiting-approval`: workflow runs are awaiting approval but the viewer cannot approve them
 *  - `completed`: every check has reached a terminal state
 *  - `none`: nothing to surface
 */
export type ChecksItemDecision =
  | {kind: 'none'}
  | {kind: 'pending'}
  | {kind: 'completed'}
  | {kind: 'approvable'; workflowApproval: PendingWorkflowApprovalRollup}
  | {kind: 'awaiting-approval'; workflowApproval: PendingWorkflowApprovalRollup}

export type ChecksItemKind = ChecksItemDecision['kind']

/**
 * Single source of truth deciding which checks For You item should render. Both the individual
 * components and any orchestrating logic should rely on this so the variants stay mutually exclusive.
 */
export function decideChecksItem(data: ValidatedChecksData | null): ChecksItemDecision {
  if (!data) {
    return {kind: 'none'}
  }

  const {statusRollup} = data
  const isPendingApproval = statusRollup.combinedState === 'PENDING_APPROVAL'
  const workflowApproval = statusRollup.pendingWorkflowApprovalRollup

  if (isPendingApproval && workflowApproval) {
    return workflowApproval.viewerCanApproveWorkflowRuns
      ? {kind: 'approvable', workflowApproval}
      : {kind: 'awaiting-approval', workflowApproval}
  }

  const hasPendingChecks = statusRollup.summary.some(check => isPendingStatus(check.state))
  if (hasPendingChecks && !isPendingApproval) {
    return {kind: 'pending'}
  }

  if (!hasPendingChecks && statusRollup.summary.length > 0) {
    return {kind: 'completed'}
  }

  return {kind: 'none'}
}
